import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { CurrencyPipe } from '@angular/common';
import { Subject, Subscription } from 'rxjs';
import { ECLService } from '@lmc-app/core/services/ecl.service';

@Component({
	selector: 'app-loan-movement',
	templateUrl: './loan-movement.component.html',
	styleUrls: ['./loan-movement.component.scss'],
})
export class LoanMovementComponent implements OnInit, OnDestroy {
	private parentSub = new Subscription();
	private readonly _onDestroy = new Subject<void>();

	protected isLoading: boolean = false;
	protected readonly inputDateForm = new FormGroup({
		dateFrom: new FormControl(null),
		dateTo: new FormControl(null),
	});

	protected balance = {
		"stage1": {
			"low": {
				"stage1": {
					"low": 10124828.24,
					"normal": 356375.61
				},
				"stage2": {
					"normal": 643438.64,
					"high": 1173927.08
				},
				"stage3": {
					"normal": 73914.75,
					"high": 0
				},
				"stage4": {
					"Writen off": 786330.11
				},
				"total": 0,
				"pd_macro_overlay": 1.1,
				"lgd": 0.85,
				"weighted_lgd": 1.01,
				"base_pd": 0.0637,
				"adjusted_pd": 0.07007000000000001,
				"adjusted_lgd": 0,
			},
			"normal": {
				"stage1": {
					"low": 0,
					"normal": 4971114.78
				},
				"stage2": {
					"normal": 218634.05,
					"high": 445964.81
				},
				"stage3": {
					"normal": 0,
					"high": 0
				},
				"stage4": {
					"Writen off": 296724.86
				},
				"total": 0,
				"pd_macro_overlay": 1.1,
				"lgd": 0.85,
				"weighted_lgd": 1.01,
				"base_pd": 0.0915,
				"adjusted_pd": 0.10065,
				"adjusted_lgd": 0,
			}
		},
		"stage2": {
			"normal": {
				"stage1": {
					"low": 225766.89,
					"normal": 161456.36
				},
				"stage2": {
					"normal": 194444.82,
					"high": 468082.21
				},
				"stage3": {
					"normal": 15548.86,
					"high": 0
				},
				"stage4": {
					"Writen off": 137009.77
				},
				"total": 0,
				"pd_macro_overlay": 1.1,
				"lgd": 0.85,
				"weighted_lgd": 1.01,
				"base_pd": 0.071,
				"adjusted_pd": 0.0781,
				"adjusted_lgd": 0,
			},
			"high": {
				"stage1": {
					"low": 56345.06,
					"normal": 75541.18
				},
				"stage2": {
					"normal": 31105.09,
					"high": 2058659.44
				},
				"stage3": {
					"normal": 666704.72,
					"high": 4741.69
				},
				"stage4": {
					"Writen off": 1490469.96
				},
				"total": 0,
				"pd_macro_overlay": 1.1,
				"lgd": 0.85,
				"weighted_lgd": 1.01,
				"base_pd": 19.84,
				"adjusted_pd": 0.21824000000000002,
				"adjusted_lgd": 0,
			}
		},
		"stage3": {
			"normal": {
				"stage1": {
					"low": 0,
					"normal": 0
				},
				"stage2": {
					"normal": 0,
					"high": 0
				},
				"stage3": {
					"normal": 146406.99,
					"high": 0
				},
				"stage4": {
					"Writen off": 127132.81
				},
				"total": 0,
				"pd_macro_overlay": 1,
				"lgd": 0.85,
				"weighted_lgd": 1.01,
				"base_pd": 1,
				"adjusted_pd": 1,
				"adjusted_lgd": 0,
			},
			"high": {
				"stage1": {
					"low": 0,
					"normal": 0
				},
				"stage2": {
					"normal": 0,
					"high": 0
				},
				"stage3": {
					"normal": 0,
					"high": 0
				},
				"stage4": {
					"Writen off": 96707.12
				},
				"total": 0,
				"pd_macro_overlay": 1,
				"lgd": 0.85,
				"weighted_lgd": 1.01,
				"base_pd": 0.9263,
				"adjusted_pd": 1,
				"adjusted_lgd": 0,
			}
		}
	};

	protected formatter = new Intl.NumberFormat('en-US', {
		style: 'percent',
		minimumFractionDigits: 0,
		maximumFractionDigits: 2,
	});

	protected readonly title: string = "Loan Movement";

	public constructor(
		private eclService: ECLService,
		protected readonly currencyFormatter: CurrencyPipe,
	) { }

	public ngOnInit(): void {
		this.inputDateForm.controls.dateFrom.setValue(new Date("2025-01-01"));
		this.inputDateForm.controls.dateTo.setValue(new Date("2026-01-01"));
	}

	public ngOnDestroy(): void {
		this.parentSub.unsubscribe();
		this._onDestroy.next();
		this._onDestroy.complete();
	}

	protected getBalance() {
		const inputDate1 = this.inputDateForm.controls.dateFrom.value.toISOString().substring(0, 10);
		const inputDate2 = this.inputDateForm.controls.dateTo.value.toISOString().substring(0, 10);
		this.isLoading = true;
		this.parentSub.add(this.eclService.getLoanMovement(inputDate1, inputDate2).subscribe((res: any) => {
			this.isLoading = false;
			if (res) {
				this.balance = res;
				this.balance.stage1.low.total = this.balance.stage1.low.stage1.low + this.balance.stage1.low.stage1.normal + this.balance.stage1.low.stage2.normal + this.balance.stage1.low.stage2.high + this.balance.stage1.low.stage3.normal + this.balance.stage1.low.stage3.high;
				this.balance.stage1.low.base_pd = (this.balance.stage1.low.stage3.normal + this.balance.stage1.low.stage3.high + this.balance.stage1.low.stage4["Writen off"]) / this.balance.stage1.low.total;
				this.balance.stage1.low.adjusted_pd = this.balance.stage1.low.base_pd * this.balance.stage1.low.pd_macro_overlay;
				this.balance.stage1.low.adjusted_lgd = this.balance.stage1.low.lgd * this.balance.stage1.low.weighted_lgd;

				this.balance.stage1.normal.total = this.balance.stage1.normal.stage1.low + this.balance.stage1.normal.stage1.normal + this.balance.stage1.normal.stage2.normal + this.balance.stage1.normal.stage2.high + this.balance.stage1.normal.stage3.normal + this.balance.stage1.normal.stage3.high;
				this.balance.stage1.normal.base_pd = (this.balance.stage1.normal.stage3.normal + this.balance.stage1.normal.stage3.high + this.balance.stage1.normal.stage4["Writen off"]) / this.balance.stage1.normal.total;
				this.balance.stage1.normal.adjusted_pd = this.balance.stage1.normal.base_pd * this.balance.stage1.normal.pd_macro_overlay;
				this.balance.stage1.normal.adjusted_lgd = this.balance.stage1.normal.lgd * this.balance.stage1.normal.weighted_lgd;

				this.balance.stage2.normal.total = this.balance.stage2.normal.stage1.low + this.balance.stage2.normal.stage1.normal + this.balance.stage2.normal.stage2.normal + this.balance.stage2.normal.stage2.high + this.balance.stage2.normal.stage3.normal + this.balance.stage2.normal.stage3.high;
				this.balance.stage2.normal.base_pd = (this.balance.stage2.normal.stage3.normal + this.balance.stage2.normal.stage3.high + this.balance.stage2.normal.stage4["Writen off"]) / this.balance.stage2.normal.total;
				this.balance.stage2.normal.adjusted_pd = this.balance.stage2.normal.base_pd * this.balance.stage2.normal.pd_macro_overlay;
				this.balance.stage2.normal.adjusted_lgd = this.balance.stage2.normal.lgd * this.balance.stage2.normal.weighted_lgd;

				this.balance.stage2.high.total = this.balance.stage2.high.stage1.low + this.balance.stage2.high.stage1.normal + this.balance.stage2.high.stage2.normal + this.balance.stage2.normal.stage2.high + this.balance.stage2.high.stage3.normal + this.balance.stage2.high.stage3.high;
				this.balance.stage2.high.base_pd = (this.balance.stage2.high.stage3.normal + this.balance.stage2.high.stage3.high + this.balance.stage2.high.stage4["Writen off"]) / this.balance.stage2.normal.total;
				this.balance.stage2.high.adjusted_pd = this.balance.stage2.high.base_pd * this.balance.stage2.high.pd_macro_overlay;
				this.balance.stage2.high.adjusted_lgd = this.balance.stage2.high.lgd * this.balance.stage2.high.weighted_lgd;

				this.balance.stage3.normal.total = this.balance.stage3.normal.stage1.low + this.balance.stage3.normal.stage1.normal + this.balance.stage3.normal.stage2.normal + this.balance.stage3.normal.stage2.high + this.balance.stage2.normal.stage3.normal + this.balance.stage2.normal.stage3.high;
				this.balance.stage3.normal.base_pd = (this.balance.stage3.normal.stage3.normal + this.balance.stage3.normal.stage3.high + this.balance.stage3.normal.stage4["Writen off"]) / this.balance.stage3.normal.total;
				this.balance.stage3.normal.adjusted_pd = this.balance.stage3.normal.base_pd * this.balance.stage3.normal.pd_macro_overlay;
				this.balance.stage3.normal.adjusted_lgd = this.balance.stage3.normal.lgd * this.balance.stage3.normal.weighted_lgd;

				this.balance.stage3.high.total = this.balance.stage3.high.stage1.low + this.balance.stage3.high.stage1.normal + this.balance.stage3.high.stage2.normal + this.balance.stage3.normal.stage2.high + this.balance.stage2.high.stage3.normal + this.balance.stage2.high.stage3.high;
				this.balance.stage3.high.base_pd = (this.balance.stage3.high.stage3.normal + this.balance.stage3.high.stage3.high + this.balance.stage3.high.stage4["Writen off"]) / this.balance.stage3.normal.total;
				this.balance.stage3.high.adjusted_pd = this.balance.stage3.high.base_pd * this.balance.stage3.high.pd_macro_overlay;
				this.balance.stage3.high.adjusted_lgd = this.balance.stage3.high.lgd * this.balance.stage3.high.weighted_lgd;
			}
		}));
	}
}
